import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom, Observable } from 'rxjs';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AcompanhamentoService } from 'app/services/acompanhamento.service';
import {
  AnexoModeloDTO,
  AnexoTarefaDTO,
  ModeloTarefaDTO,
  ResumoPreSessao,
  StatusTarefaTerapeutica,
  TipoAnexoTarefa,
  TarefaTerapeuticaDTO
} from 'app/models/acompanhamento.model';
import { GravacaoAudio } from 'app/shared/audio/audio-recorder.component';
import { NotificacaoService } from 'app/services/notificacao.service';
import { SweetAlertService } from 'app/services/sweetalert.service';

/**
 * Modal do painel do psicólogo com o resumo pré-sessão do paciente e o CRUD
 * de tarefas terapêuticas (criar, listar pendentes/concluídas, cancelar).
 *
 * Acionado a partir da tabela do "Acompanhamento entre sessões" e da ficha do
 * paciente no prontuário.
 */
@Component({
  selector: 'app-acompanhamento-modal',
  templateUrl: './acompanhamento-modal.component.html',
  styleUrls: ['./acompanhamento-modal.component.scss'],
  standalone: false
})
export class AcompanhamentoModalComponent implements OnInit {
  @Input() clienteId!: number;
  @Input() nomeCliente: string = '';

  readonly StatusTarefa = StatusTarefaTerapeutica;
  readonly TipoAnexo = TipoAnexoTarefa;

  loading = true;
  salvando = false;
  cancelandoId: number | null = null;
  anexoOcupadoId: number | null = null;
  linkFormTarefaId: number | null = null;
  novoLinkUrl = '';
  novoLinkTitulo = '';
  erro: string | null = null;

  resumo: ResumoPreSessao | null = null;
  tarefas: TarefaTerapeuticaDTO[] = [];

  mostrarFormularioTarefa = false;
  novaTarefaForm: FormGroup;
  audioNovaTarefa: GravacaoAudio | null = null;
  // Anexos/links pendentes (adicionados antes de salvar a tarefa)
  anexosPendentesTarefa: File[] = [];
  linksPendentesTarefa: { url: string; titulo: string }[] = [];
  mostrarLinkFormNovaTarefa = false;
  novaTarefaLinkUrl = '';
  novaTarefaLinkTitulo = '';

  // Modelos (biblioteca reutilizável)
  modelos: ModeloTarefaDTO[] = [];
  mostrarModelos = false;
  mostrarFormularioModelo = false;
  novoModeloForm: FormGroup;
  audioNovoModelo: GravacaoAudio | null = null;
  anexosPendentesModelo: File[] = [];
  linksPendentesModelo: { url: string; titulo: string }[] = [];
  mostrarLinkFormNovoModelo = false;
  novoModeloLinkUrl = '';
  novoModeloLinkTitulo = '';
  salvandoModelo = false;
  aplicandoModeloId: number | null = null;
  excluindoModeloId: number | null = null;
  anexoOcupadoModeloId: number | null = null;

  constructor(
    private activeModal: NgbActiveModal,
    private service: AcompanhamentoService,
    private notificacao: NotificacaoService,
    private swal: SweetAlertService,
    private fb: FormBuilder
  ) {
    // Descrição é opcional: a orientação pode vir só em áudio.
    this.novaTarefaForm = this.fb.group({
      titulo: ['', [Validators.required, Validators.maxLength(150)]],
      descricao: ['', [Validators.maxLength(2000)]],
      dataPrazo: [null]
    });
    this.novoModeloForm = this.fb.group({
      titulo: ['', [Validators.required, Validators.maxLength(150)]],
      descricao: ['', [Validators.maxLength(2000)]]
    });
  }

  get faltaConteudoModelo(): boolean {
    const descricao = (this.novoModeloForm.value.descricao ?? '').trim();
    return descricao.length === 0 && !this.audioNovoModelo
      && this.anexosPendentesModelo.length === 0 && this.linksPendentesModelo.length === 0;
  }

  onArquivoNovoModelo(event: Event): void {
    const input = event.target as HTMLInputElement;
    const arquivo = input.files?.[0];
    input.value = '';
    if (arquivo) this.anexosPendentesModelo = [...this.anexosPendentesModelo, arquivo];
  }

  removerAnexoPendenteModelo(i: number): void {
    this.anexosPendentesModelo = this.anexosPendentesModelo.filter((_, idx) => idx !== i);
  }

  adicionarLinkPendenteModelo(): void {
    const url = this.novoModeloLinkUrl.trim();
    if (!url) return;
    this.linksPendentesModelo = [...this.linksPendentesModelo, { url, titulo: this.novoModeloLinkTitulo.trim() }];
    this.novoModeloLinkUrl = '';
    this.novoModeloLinkTitulo = '';
    this.mostrarLinkFormNovoModelo = false;
  }

  removerLinkPendenteModelo(i: number): void {
    this.linksPendentesModelo = this.linksPendentesModelo.filter((_, idx) => idx !== i);
  }

  async abrirAnexoModelo(a: AnexoModeloDTO): Promise<void> {
    try {
      const blob = await firstValueFrom(this.service.obterAnexoModelo(a.id));
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      this.notificacao.mensagemErro('Não foi possível abrir o anexo.');
    }
  }

  async removerAnexoModelo(m: ModeloTarefaDTO, a: AnexoModeloDTO): Promise<void> {
    const result = await this.swal.alertaConfirmacao(
      'Remover anexo?', a.nomeOriginal, 'Sim, remover', 'Voltar');
    if (!result?.isConfirmed) return;
    this.anexoOcupadoModeloId = m.id;
    try {
      const atualizado = await this.service.removerAnexoModelo(a.id);
      this.modelos = this.modelos.map(x => (x.id === atualizado.id ? atualizado : x));
    } catch (e: any) {
      this.notificacao.mensagemErro(e?.error?.mensagem ?? 'Não foi possível remover o anexo.');
    } finally {
      this.anexoOcupadoModeloId = null;
    }
  }

  /** Exige pelo menos descrição, áudio, anexo ou link. */
  get faltaConteudoTarefa(): boolean {
    const descricao = (this.novaTarefaForm.value.descricao ?? '').trim();
    return descricao.length === 0 && !this.audioNovaTarefa
      && this.anexosPendentesTarefa.length === 0 && this.linksPendentesTarefa.length === 0;
  }

  onArquivoNovaTarefa(event: Event): void {
    const input = event.target as HTMLInputElement;
    const arquivo = input.files?.[0];
    input.value = '';
    if (arquivo) this.anexosPendentesTarefa = [...this.anexosPendentesTarefa, arquivo];
  }

  removerAnexoPendenteTarefa(i: number): void {
    this.anexosPendentesTarefa = this.anexosPendentesTarefa.filter((_, idx) => idx !== i);
  }

  adicionarLinkPendenteTarefa(): void {
    const url = this.novaTarefaLinkUrl.trim();
    if (!url) return;
    this.linksPendentesTarefa = [...this.linksPendentesTarefa, { url, titulo: this.novaTarefaLinkTitulo.trim() }];
    this.novaTarefaLinkUrl = '';
    this.novaTarefaLinkTitulo = '';
    this.mostrarLinkFormNovaTarefa = false;
  }

  removerLinkPendenteTarefa(i: number): void {
    this.linksPendentesTarefa = this.linksPendentesTarefa.filter((_, idx) => idx !== i);
  }

  aoGravarAudioTarefa(g: GravacaoAudio | null): void {
    this.audioNovaTarefa = g;
  }

  carregadorAudioInstrucao(t: TarefaTerapeuticaDTO): () => Observable<Blob> {
    return () => this.service.obterAudioTarefa(t.id);
  }

  carregadorRespostaAudio(t: TarefaTerapeuticaDTO): () => Observable<Blob> {
    return () => this.service.obterRespostaAudioTarefa(t.id);
  }

  async ngOnInit(): Promise<void> {
    await this.carregar();
  }

  private async carregar(): Promise<void> {
    this.loading = true;
    this.erro = null;
    try {
      const [resumo, tarefas, modelos] = await Promise.all([
        this.service.obterResumoPreSessao(this.clienteId),
        this.service.listarTarefas(this.clienteId),
        this.service.listarModelos()
      ]);
      this.resumo = resumo;
      this.tarefas = tarefas;
      this.modelos = modelos;
      if (!this.nomeCliente) this.nomeCliente = resumo.nomeCliente;
    } catch (e: any) {
      this.erro = e?.error?.mensagem ?? 'Não foi possível carregar as informações.';
    } finally {
      this.loading = false;
    }
  }

  get tarefasPendentes(): TarefaTerapeuticaDTO[] {
    return this.tarefas.filter(t => t.status === StatusTarefaTerapeutica.Pendente);
  }

  get tarefasConcluidas(): TarefaTerapeuticaDTO[] {
    return this.tarefas.filter(t => t.status === StatusTarefaTerapeutica.Concluida);
  }

  get tarefasCanceladas(): TarefaTerapeuticaDTO[] {
    return this.tarefas.filter(t => t.status === StatusTarefaTerapeutica.Cancelada);
  }

  abrirFormularioTarefa(): void {
    this.mostrarFormularioTarefa = true;
    this.resetarNovaTarefa();
  }

  fecharFormularioTarefa(): void {
    this.mostrarFormularioTarefa = false;
    this.resetarNovaTarefa();
  }

  private resetarNovaTarefa(): void {
    this.audioNovaTarefa = null;
    this.anexosPendentesTarefa = [];
    this.linksPendentesTarefa = [];
    this.mostrarLinkFormNovaTarefa = false;
    this.novaTarefaLinkUrl = '';
    this.novaTarefaLinkTitulo = '';
    this.novaTarefaForm.reset({ titulo: '', descricao: '', dataPrazo: null });
  }

  async salvarNovaTarefa(): Promise<void> {
    if (this.novaTarefaForm.invalid || this.faltaConteudoTarefa) {
      this.novaTarefaForm.markAllAsTouched();
      return;
    }
    this.salvando = true;
    try {
      const v = this.novaTarefaForm.value;
      let criada = await this.service.criarTarefa({
        clienteId: this.clienteId,
        titulo: (v.titulo ?? '').trim(),
        descricao: (v.descricao ?? '').trim(),
        dataPrazo: v.dataPrazo ? v.dataPrazo : null
      });
      if (this.audioNovaTarefa) {
        criada = await this.service.anexarAudioTarefa(
          criada.id, this.audioNovaTarefa.blob, this.audioNovaTarefa.duracaoSegundos);
      }
      for (const arquivo of this.anexosPendentesTarefa) {
        criada = await this.service.anexarArquivoTarefa(criada.id, arquivo);
      }
      for (const link of this.linksPendentesTarefa) {
        criada = await this.service.anexarLinkTarefa(criada.id, link.url, link.titulo || undefined);
      }
      this.tarefas = [criada, ...this.tarefas];
      this.notificacao.mensagemSucesso('Tarefa terapêutica criada.');
      this.fecharFormularioTarefa();
    } catch (e: any) {
      this.notificacao.mensagemErro(e?.error?.mensagem ?? 'Não foi possível criar a tarefa.');
    } finally {
      this.salvando = false;
    }
  }

  async cancelarTarefa(t: TarefaTerapeuticaDTO): Promise<void> {
    if (this.cancelandoId != null) return;
    const result = await this.swal.alertaConfirmacao(
      'Cancelar tarefa?',
      `A tarefa "${t.titulo}" será cancelada.`,
      'Sim, cancelar',
      'Voltar'
    );
    if (!result?.isConfirmed) return;
    this.cancelandoId = t.id;
    try {
      const atualizada = await this.service.cancelarTarefa(t.id);
      this.tarefas = this.tarefas.map(x => x.id === t.id ? atualizada : x);
      this.notificacao.mensagemSucesso('Tarefa cancelada.');
    } catch (e: any) {
      this.notificacao.mensagemErro(e?.error?.mensagem ?? 'Não foi possível cancelar a tarefa.');
    } finally {
      this.cancelandoId = null;
    }
  }

  // ---------- Modelos ----------

  toggleModelos(): void {
    this.mostrarModelos = !this.mostrarModelos;
  }

  abrirFormularioModelo(): void {
    this.mostrarFormularioModelo = true;
    this.resetarNovoModelo();
  }

  fecharFormularioModelo(): void {
    this.mostrarFormularioModelo = false;
    this.resetarNovoModelo();
  }

  private resetarNovoModelo(): void {
    this.audioNovoModelo = null;
    this.anexosPendentesModelo = [];
    this.linksPendentesModelo = [];
    this.mostrarLinkFormNovoModelo = false;
    this.novoModeloLinkUrl = '';
    this.novoModeloLinkTitulo = '';
    this.novoModeloForm.reset({ titulo: '', descricao: '' });
  }

  aoGravarAudioModelo(g: GravacaoAudio | null): void {
    this.audioNovoModelo = g;
  }

  async salvarNovoModelo(): Promise<void> {
    if (this.novoModeloForm.invalid || this.faltaConteudoModelo) {
      this.novoModeloForm.markAllAsTouched();
      return;
    }
    this.salvandoModelo = true;
    try {
      const v = this.novoModeloForm.value;
      let modelo = await this.service.criarModelo((v.titulo ?? '').trim(), (v.descricao ?? '').trim() || undefined);
      if (this.audioNovoModelo) {
        modelo = await this.service.anexarAudioModelo(modelo.id, this.audioNovoModelo.blob, this.audioNovoModelo.duracaoSegundos);
      }
      for (const arquivo of this.anexosPendentesModelo) {
        modelo = await this.service.anexarArquivoModelo(modelo.id, arquivo);
      }
      for (const link of this.linksPendentesModelo) {
        modelo = await this.service.anexarLinkModelo(modelo.id, link.url, link.titulo || undefined);
      }
      this.modelos = [modelo, ...this.modelos];
      this.notificacao.mensagemSucesso('Modelo salvo na biblioteca.');
      this.fecharFormularioModelo();
    } catch (e: any) {
      this.notificacao.mensagemErro(e?.error?.mensagem ?? 'Não foi possível salvar o modelo.');
    } finally {
      this.salvandoModelo = false;
    }
  }

  async aplicarModelo(m: ModeloTarefaDTO): Promise<void> {
    if (this.aplicandoModeloId != null) return;
    this.aplicandoModeloId = m.id;
    try {
      const criada = await this.service.aplicarModelo(m.id, this.clienteId);
      this.tarefas = [criada, ...this.tarefas];
      this.notificacao.mensagemSucesso(`Tarefa "${m.titulo}" criada para ${this.nomeCliente}.`);
    } catch (e: any) {
      this.notificacao.mensagemErro(e?.error?.mensagem ?? 'Não foi possível aplicar o modelo.');
    } finally {
      this.aplicandoModeloId = null;
    }
  }

  async excluirModelo(m: ModeloTarefaDTO): Promise<void> {
    const result = await this.swal.alertaConfirmacao(
      'Excluir modelo?', `O modelo "${m.titulo}" será removido da biblioteca.`, 'Sim, excluir', 'Voltar');
    if (!result?.isConfirmed) return;
    this.excluindoModeloId = m.id;
    try {
      await this.service.excluirModelo(m.id);
      this.modelos = this.modelos.filter(x => x.id !== m.id);
    } catch (e: any) {
      this.notificacao.mensagemErro(e?.error?.mensagem ?? 'Não foi possível excluir o modelo.');
    } finally {
      this.excluindoModeloId = null;
    }
  }

  // ---------- Anexos ----------

  abrirLinkForm(t: TarefaTerapeuticaDTO): void {
    this.linkFormTarefaId = t.id;
    this.novoLinkUrl = '';
    this.novoLinkTitulo = '';
  }

  fecharLinkForm(): void {
    this.linkFormTarefaId = null;
    this.novoLinkUrl = '';
    this.novoLinkTitulo = '';
  }

  async onArquivoSelecionado(t: TarefaTerapeuticaDTO, event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const arquivo = input.files?.[0];
    input.value = ''; // permite re-selecionar o mesmo arquivo
    if (!arquivo) return;

    this.anexoOcupadoId = t.id;
    try {
      const atualizada = await this.service.anexarArquivoTarefa(t.id, arquivo);
      this.substituirTarefa(atualizada);
      this.notificacao.mensagemSucesso('Anexo adicionado.');
    } catch (e: any) {
      this.notificacao.mensagemErro(e?.error?.mensagem ?? 'Não foi possível anexar o arquivo.');
    } finally {
      this.anexoOcupadoId = null;
    }
  }

  async adicionarLink(t: TarefaTerapeuticaDTO): Promise<void> {
    const url = this.novoLinkUrl.trim();
    if (!url) return;
    this.anexoOcupadoId = t.id;
    try {
      const atualizada = await this.service.anexarLinkTarefa(t.id, url, this.novoLinkTitulo.trim() || undefined);
      this.substituirTarefa(atualizada);
      this.fecharLinkForm();
      this.notificacao.mensagemSucesso('Link adicionado.');
    } catch (e: any) {
      this.notificacao.mensagemErro(e?.error?.mensagem ?? 'Não foi possível adicionar o link.');
    } finally {
      this.anexoOcupadoId = null;
    }
  }

  async removerAnexo(t: TarefaTerapeuticaDTO, anexo: AnexoTarefaDTO): Promise<void> {
    const result = await this.swal.alertaConfirmacao(
      'Remover anexo?', anexo.nomeOriginal, 'Sim, remover', 'Voltar');
    if (!result?.isConfirmed) return;
    this.anexoOcupadoId = t.id;
    try {
      const atualizada = await this.service.removerAnexoTarefa(anexo.id);
      this.substituirTarefa(atualizada);
    } catch (e: any) {
      this.notificacao.mensagemErro(e?.error?.mensagem ?? 'Não foi possível remover o anexo.');
    } finally {
      this.anexoOcupadoId = null;
    }
  }

  async abrirAnexoArquivo(anexo: AnexoTarefaDTO): Promise<void> {
    try {
      const blob = await firstValueFrom(this.service.obterAnexoTarefa(anexo.id));
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      this.notificacao.mensagemErro('Não foi possível abrir o anexo.');
    }
  }

  private substituirTarefa(atualizada: TarefaTerapeuticaDTO): void {
    this.tarefas = this.tarefas.map(x => (x.id === atualizada.id ? atualizada : x));
  }

  classeHumor(humor: number | null | undefined): string {
    if (humor == null) return 'humor-neutro';
    if (humor <= 2) return 'humor-baixo';
    if (humor === 3) return 'humor-neutro';
    return 'humor-bom';
  }

  emojiHumor(humor: number | null | undefined): string {
    if (humor == null) return '–';
    return ['😢', '😞', '😐', '🙂', '😄'][humor - 1] ?? '–';
  }

  fechar(): void {
    this.activeModal.dismiss();
  }
}
